/**
 * SupportPage — Task #14-FE, Redesign nach v2-final/support.html
 *
 * Zwei-Spalten-Layout: Kontaktformular (Thema-Select + Nachricht) links,
 * FAQ-Accordion rechts. FAQ beschreibt nur echte Features — kein
 * Wallet-Connect, kein Auto-Payout, 30-Tage-Löschung (SPEC/ACCEPTANCE).
 *
 * Contract-Realität: POST /api/v2/support/contact kennt nur { subject,
 * message } (useSupportContact). Das Thema-Select liefert den `subject`-
 * Text. Die "Diagnoseinfos anhängen"-Checkbox ist wie im Prototyp rein
 * visuell (kein Diagnose-Feld im Contract) — checked by default, nicht an
 * die Mutation angeschlossen.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Send, CheckCircle, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useSupportContact } from './useSupportContact'
import { SupportTicketsSection } from './SupportTicketsSection'
import './support.css'

const TOPIC_KEYS = ['account', 'hotspotWallet', 'billing', 'api', 'other'] as const
const FAQ_KEYS = ['addHotspot', 'importVsWatch', 'autoPayout', 'poc', 'deleteAccount'] as const

export function SupportPage() {
  const { t } = useTranslation('support')
  const topics = TOPIC_KEYS.map((key) => ({ key, label: t(`contact.topic.options.${key}`) }))
  const [subject, setSubject] = useState(topics[0].label)
  const [message, setMessage] = useState('')
  const [clientError, setClientError] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const { mutate, isPending, isSuccess, isError, reset } = useSupportContact()

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (!subject.trim()) {
      setClientError(t('validation.subjectRequired'))
      return
    }
    if (!message.trim()) {
      setClientError(t('validation.messageRequired'))
      return
    }

    setClientError(null)
    mutate(
      { subject: subject.trim(), message: message.trim() },
      { onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-tickets'] }) }
    )
  }

  function handleReset() {
    setMessage('')
    setClientError(null)
    reset()
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>{t('page.title')}</h1>
          <p className="muted" style={{ margin: 0 }}>{t('page.subtitle')}</p>
        </div>
      </div>

      <div className="support-grid">
        <div className="card card-pad">
          <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>{t('contact.title')}</h3>

          {isSuccess ? (
            <div data-testid="support-success" className="row" style={{ alignItems: 'flex-start', gap: 12 }}>
              <CheckCircle size={20} aria-hidden="true" style={{ color: 'var(--success)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('success.title')}</div>
                <p className="muted" style={{ margin: 0, fontSize: 13 }}>{t('success.message')}</p>
                <button onClick={handleReset} className="btn btn-sm" style={{ marginTop: 12 }}>
                  {t('contact.newRequest')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {clientError && (
                <p data-testid="support-client-error" style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 13 }}>
                  {clientError}
                </p>
              )}
              {isError && (
                <div data-testid="support-error" className="row" style={{ alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
                  <AlertCircle size={16} aria-hidden="true" style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                  <p className="muted" style={{ margin: 0, fontSize: 13 }}>{t('error.message')}</p>
                </div>
              )}

              <div className="support-frow">
                <label htmlFor="support-topic">{t('contact.topic.label')}</label>
                <select
                  id="support-topic"
                  className="select"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={isPending}
                >
                  {topics.map((topic) => (
                    <option key={topic.key} value={topic.label}>{topic.label}</option>
                  ))}
                </select>
              </div>

              <div className="support-frow">
                <label htmlFor="support-message">{t('contact.message.label')}</label>
                <textarea
                  id="support-message"
                  className="textarea"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={5000}
                  placeholder={t('contact.message.placeholder')}
                  disabled={isPending}
                />
              </div>

              <label className="row" style={{ gap: 8, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" defaultChecked style={{ accentColor: 'var(--primary)' }} />
                {t('contact.diagnostic')}
              </label>

              <button type="submit" className="btn btn-primary" disabled={isPending} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Send size={16} aria-hidden="true" />
                {isPending ? t('contact.submitting') : t('contact.submit')}
              </button>
            </form>
          )}
        </div>

        <div className="card card-pad">
          <h3 style={{ margin: '0 0 6px', fontSize: 14 }}>{t('faq.title')}</h3>
          {FAQ_KEYS.map((key) => (
            <details key={key} className="support-faq">
              <summary>
                {t(`faq.items.${key}.question`)}
                <span aria-hidden="true">+</span>
              </summary>
              <p>{t(`faq.items.${key}.answer`)}</p>
            </details>
          ))}
          <details className="support-faq">
            <summary>
              {t('faq.items.plan.question')}
              <span aria-hidden="true">+</span>
            </summary>
            <p>
              {t('faq.items.plan.answerPrefix')}
              <Link to="/plans" style={{ color: 'var(--primary)' }}>{t('faq.items.plan.answerLink')}</Link>
              {t('faq.items.plan.answerSuffix')}
            </p>
          </details>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <SupportTicketsSection />
      </div>
    </div>
  )
}
